<template>
  <VDialogV3
      id="feed-dialog"
      position="right"
      :hide-footer="!showAddComment"
      width="600px"
      height="100vh"
      close-on-overlay-click
      :loading="false"
  >
    <template #header>
      <div class="title" :title="headerTitle">
        {{ headerTitle }}
      </div>
    </template>

    <template #toolbar>
      <VButton
        v-if="showDownloadBtn"
        title="Скачать историю"
        material="download"
        @click="downloadLogs"
      />
    </template>

    <Feed
        ref="feed"
        :parent-entities="parentEntity"
        :records="externalRecords"
        :feed-types="feedTypes"
    />

    <template #footer v-if="showAddComment">
      <VCommentCreateForm
          :parent-entity="parentEntity"
          @comment:created="renewData"
      />
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import EntityPointer           from '@/domains/EntityPointer/EntityPointer';
import FeedRecord              from '@/core/services/Feeds/FeedRecord';
import ProductionPlansService  from '@/domains/ProductionPlans/ProductionPlansService';

import Feed                    from '@/core/services/Feeds/components/Feed/Feed.vue';

export default defineComponent({
  name: 'FeedDialog',

  components: {
    Feed,
  },

  props: {
    entityTypeName: {
      type:    String,
      default: null,
    },
    entityId: {
      type:    [String, Number] as PropType<string | number>,
      default: null,
    },
    feedTypes: {
      type:    Array as PropType<string[]>,
      default: () => [],
    },
    showAddComment: {
      type:    Boolean,
      default: true,
    },

    // ============================================================
    // История товара в производственных планах — единственный случай,
    // когда история грузится не через feeds.get, а через
    // productionPlans.getProductLogs, и доступна выгрузка в файл.
    // Включается передачей productCode.
    // ============================================================

    /** Заголовок диалога. */
    title: {
      type:    String,
      default: null,
    },
    /** Код товара — включает режим истории товара произв. плана. */
    productCode: {
      type:    String,
      default: null,
    },
    /** Плановая дата прихода (для конкретной ячейки месяца). */
    plannedArrivalDate: {
      type:    String,
      default: null,
    },
    /** Показывать кнопку скачивания истории. */
    showDownloadBtn: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['change'],

  data() {
    return {
      externalRecords: null as FeedRecord[] | null,
    };
  },

  computed: {
    /** Режим истории товара произв. плана (грузим через сервис планов). */
    isProductLogsMode(): boolean {
      return this.productCode !== null;
    },

    /** Указатель родителя для обычного feeds.get. В режиме планов не используется. */
    parentEntity(): EntityPointer | null {
      if (this.isProductLogsMode) {
        return null;
      }
      else {
        return new EntityPointer(this.entityTypeName, this.entityId);
      }
    },

    headerTitle(): string {
      if (this.title) {
        return `История ${this.title}`;
      }
      else {
        return 'История';
      }
    },
  },

  mounted() {
    if (this.isProductLogsMode) {
      this.loadProductLogs();
    }
  },

  methods: {
    /** Грузит историю товара произв. плана и отдаёт записи в Feed через records. */
    async loadProductLogs() {
      this.externalRecords = await ProductionPlansService.getProductLogs(
        this.productCode,
        this.plannedArrivalDate ?? undefined,
      );
    },

    renewData() {
      this.$emit('change');

      this.$refs.feed.renewData();
    },

    /** Выгрузка истории товара в файл (только режим планов). */
    async downloadLogs() {
      await ProductionPlansService.exportLogs(this.title ?? '', this.$refs.feed.getRecords());
    },
  },
});
</script>
